/** `/ai-changes/proposals/:id` — the review page of an AI-editing proposal (backend metaplan A8;
 *  M24 rev 3): an MCP client's, or the console agent's (A9, origin `chat`).
 *
 *  What an MCP tool's `apply` links to on a production tenant — and what the console agent's
 *  `request_apply` hands over on every tenant (it proposes only, AIE Q6): who asked through which
 *  client and why, the changes it proposes, the preview the requester saw (synthetic guests), and —
 *  the only place they exist — renders of the change on REAL bookings, before → after. A person
 *  holding `console_notification_mcp_write` applies or rejects the pending change here. A franchise
 *  licensee's copy of its parent's notifications (P7) also shows what the copy changes for it
 *  (`preview_summary.franchise`, `FranchiseCopySummary`); a notification's settings change (P3),
 *  per item, who starts and stops receiving it and what else it silences or frees
 *  (`preview_summary.settings`, `SettingsChangeSummary`) — and its real-booking renders say
 *  whether delivery sends before → after. A content or vocabulary change (client MCP WPS P3-16 /
 *  FE-07) shows the records its preview recorded, per kind, from the summary's own description
 *  (`ReviewSummarySections`): new kinds need no change here.
 *
 *  `ProposalReview` is the page's body; the console agent page embeds it as its review panel
 *  (`embedded`), next to the chat. */
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';

import {
  aiEditKeys,
  proposalRenders,
  retrieveProposal,
  type AIProposalRenderChannel,
  type AIProposalRenders,
  type AIProposalReview,
} from '@/api/aiEdit';
import { ConsoleApiError } from '@/api/client';
import { PageHeader } from '@/components/PageHeader';
import { Field, FieldGrid, SectionCard } from '@/components/ReadView';
import { UntrustedInline } from '@/components/UntrustedText';
import { RenderedBodyView } from '@/features/notifications/test-render/RenderedBodyView';
import { formatDate } from '@/utils/format';

import { FranchiseCopySummary } from './FranchiseCopySummary';
import { ITEM_ACTION } from './itemActions';
import { PendingChangeCard } from './PendingChangeCard';
import { hasReviewSections, ReviewSummarySections } from './ReviewSummarySections';
import { SettingsChangeSummary } from './SettingsChangeSummary';
import { ItemNamesLegend, REQUEST_REASON_LABEL, ReasonValue } from './theirWords';

/** The records card's title, by the summary's domain. */
const RECORDS_TITLE: Record<string, string> = {
  content: 'Content records',
  vocabulary: 'AI vocabulary entries',
};

export function AIProposalReviewPage() {
  const id = Number(useParams<{ id: string }>().id);
  return <ProposalReviewById id={id} />;
}

const isAgent = (review: AIProposalReview) => review.request.channel === 'console_agent';

/** "Console agent proposal" / "MCP proposal". */
export function proposalTitle(review: AIProposalReview): string {
  return `${isAgent(review) ? 'Console agent' : 'MCP'} proposal #${review.id}`;
}

/** Fetches and renders one proposal's review — the page, or the console agent's panel. */
export function ProposalReviewById({ id, embedded = false }: { id: number; embedded?: boolean }) {
  const query = useQuery({
    queryKey: aiEditKeys.proposal(id),
    queryFn: () => retrieveProposal(id),
    enabled: Number.isFinite(id),
  });
  if (query.isPending) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: embedded ? 2 : 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (query.isError) {
    const status = query.error instanceof ConsoleApiError ? query.error.status : 0;
    return (
      <Box>
        {!embedded && <PageHeader title="AI proposal" helpStory="ai-changes" />}
        <Alert severity={status === 404 ? 'info' : 'error'}>
          {status === 404
            ? 'There is no AI-editing proposal with this id.'
            : status === 403
              ? 'You can’t see AI changes: it needs the console_ai_changes_view permission.'
              : `Couldn’t load the proposal: ${query.error.message}`}
        </Alert>
      </Box>
    );
  }
  return <ProposalReview review={query.data} embedded={embedded} />;
}

export function ProposalReview({
  review,
  embedded = false,
}: {
  review: AIProposalReview;
  /** Inside the console agent page: no page header, a link to the full page instead. */
  embedded?: boolean;
}) {
  const { request } = review;
  const agent = isAgent(review);
  const open = review.pending_changes.filter(
    (p) => p.status === 'pending' || p.status === 'applying',
  );
  const summary = (request.preview_summary ?? {}) as {
    blast_radius?: Record<string, unknown> | null;
    notifications?: { id: number; name: string; event: string; reasons?: string[] }[];
  };
  const franchise = request.preview_summary?.franchise;
  const settings = request.preview_summary?.settings;
  // A described summary shows its counts with its records.
  const described = hasReviewSections(request.preview_summary);
  const subtitle = `${review.tenant_code ?? ''} · ${review.created_ts ? formatDate(review.created_ts) : ''}`;
  return (
    <Box sx={{ pb: embedded ? 0 : 4 }} data-proposal-review={review.id}>
      {embedded ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <Typography variant="h6" component="h2">
            {proposalTitle(review)}
          </Typography>
          <Chip size="small" label={review.status} data-proposal-status />
          <Box sx={{ flex: 1 }} />
          <Link component={RouterLink} to={`/ai-changes/proposals/${review.id}`} variant="body2">
            Open the review page
          </Link>
        </Stack>
      ) : (
        <PageHeader
          helpStory="ai-changes"
          title={proposalTitle(review)}
          titleAdornment={<Chip size="small" label={review.status} data-proposal-status />}
          subtitle={subtitle}
        />
      )}
      {open.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {agent
            ? 'Nothing has changed yet: the console agent proposes only, and its changes wait for a person to apply them here. '
            : 'Nothing has changed yet: this production tenant’s MCP changes wait for a person to apply them here. '}
          Check the real-booking renders below before applying.
        </Alert>
      )}

      <SectionCard title="Request">
        <FieldGrid>
          <Field label={REQUEST_REASON_LABEL} value={<ReasonValue text={request.reason} />} />
          <Field label="Requested by" value={request.requested_by?.email ?? '—'} />
          <Field
            label="Client"
            value={
              agent
                ? 'Console agent'
                : request.oauth_client_name || request.oauth_client_id || '—'
            }
          />
          <Field
            label="Preview"
            value={
              request.preview_ready
                ? `Ready${request.preview_ts ? ` · ${formatDate(request.preview_ts)}` : ''}`
                : 'Not previewed (or not ready)'
            }
          />
          {summary.blast_radius && !described && (
            <Field label="Blast radius" value={JSON.stringify(summary.blast_radius)} />
          )}
        </FieldGrid>
      </SectionCard>

      {franchise && <FranchiseCopySummary franchise={franchise} />}
      {Array.isArray(settings) && settings.length > 0 && (
        <SettingsChangeSummary settings={settings} />
      )}
      {described && (
        <ReviewSummarySections
          summary={request.preview_summary}
          title={RECORDS_TITLE[request.preview_summary?.domain ?? ''] ?? 'Records'}
        />
      )}

      <SectionCard title={`Pending changes (${review.pending_changes.length})`}>
        {review.pending_changes.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No apply was requested for this proposal.
          </Typography>
        ) : (
          review.pending_changes.map((p) => <PendingChangeCard key={p.id} pending={p} />)
        )}
      </SectionCard>

      <SectionCard title={`Proposed changes (${review.proposal.items.length})`}>
        <ItemNamesLegend />
        <Table size="small" aria-label="Proposed changes">
          <TableHead>
            <TableRow>
              <TableCell>Kind</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Target</TableCell>
              <TableCell>Label</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {review.proposal.items.map((item) => (
              <TableRow key={item.id}>
                <TableCell>{item.kind}</TableCell>
                <TableCell>{ITEM_ACTION[item.action]}</TableCell>
                <TableCell>{item.target_id ?? 'new'}</TableCell>
                <TableCell>
                  <UntrustedInline text={item.label} />
                </TableCell>
                <TableCell>{item.status}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </SectionCard>

      {review.change_sets.length > 0 && (
        <SectionCard title="Change sets">
          <Stack spacing={0.5}>
            {review.change_sets.map((cs) => (
              <RouterLink key={cs.id} to={`/ai-changes/${cs.id}`}>
                {cs.id.slice(0, 8)} · {cs.status}
              </RouterLink>
            ))}
          </Stack>
        </SectionCard>
      )}

      <RealBookingRenders proposalId={review.id} />
    </Box>
  );
}

/** Before → after on real bookings: computed on request (the engine applies the proposal in a
 *  rolled-back transaction and renders each booking twice), never cached across proposals. */
function RealBookingRenders({ proposalId }: { proposalId: number }) {
  const [requested, setRequested] = useState(false);
  const query = useQuery({
    queryKey: aiEditKeys.renders(proposalId),
    queryFn: () => proposalRenders(proposalId),
    enabled: requested,
    staleTime: 0,
  });
  return (
    <SectionCard
      title="Real-booking renders"
      action={
        <Button
          size="small"
          onClick={() => (requested ? void query.refetch() : setRequested(true))}
        >
          {requested ? 'Render again' : 'Render on real bookings'}
        </Button>
      }
    >
      {!requested && (
        <Typography variant="body2" color="text.secondary">
          The AI only saw invented guests. Render the change on recent real bookings to
          check it before applying. These renders contain guest data: they stay in the console.
        </Typography>
      )}
      {requested && query.isPending && <CircularProgress size={20} />}
      {query.isError && <Alert severity="error">Couldn’t render: {query.error.message}</Alert>}
      {query.data && <RendersView data={query.data} />}
    </SectionCard>
  );
}

function RendersView({ data }: { data: AIProposalRenders }) {
  if (!data.ok) {
    return (
      <Alert severity="warning" data-renders-not-ok>
        The proposal does not apply cleanly any more, so it cannot be rendered:{' '}
        {String((data.apply as { error?: string } | null)?.error ?? '')}
      </Alert>
    );
  }
  if (data.notifications.length === 0) {
    return <Typography variant="body2">No notification is affected.</Typography>;
  }
  return (
    <Stack spacing={2}>
      {data.notifications.map((nf) => (
        <Box key={nf.id} data-render-notification={nf.id}>
          <Typography variant="subtitle2">
            {nf.name}{' '}
            <Typography component="span" variant="caption">
              ({nf.event})
            </Typography>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {nf.reasons.join('; ')}
          </Typography>
          {nf.not_rendered_because && (
            <Typography variant="body2" color="text.secondary">
              Not rendered: {nf.not_rendered_because}
            </Typography>
          )}
          {nf.renders.map((render) => (
            <Box key={render.booking} sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {render.label}
                {render.language ? ` · ${render.language}` : ''}
              </Typography>
              {render.error && <Alert severity="error">{render.error}</Alert>}
              {render.sent && (
                <SentBeforeAfter sent={render.sent} because={render.not_sent_because} />
              )}
              {render.channels.map((ch) => (
                <ChannelBeforeAfter key={`${render.booking}-${ch.delivery_method}`} channel={ch} />
              ))}
            </Box>
          ))}
        </Box>
      ))}
    </Stack>
  );
}

type Render = AIProposalRenders['notifications'][number]['renders'][number];

/** A settings change's render (P3): would delivery send it on this booking, before → after, and
 *  the matcher's reason where it does not. */
function SentBeforeAfter({
  sent,
  because,
}: {
  sent: NonNullable<Render['sent']>;
  because: Render['not_sent_because'];
}) {
  const word = (v: boolean | null | undefined) =>
    v === true ? 'sends' : v === false ? 'not sent' : '—';
  return (
    <Box sx={{ mt: 0.5 }} data-render-sent>
      <Typography variant="body2">{`Delivery: ${word(sent.before)} → ${word(sent.after)}`}</Typography>
      {because?.before && (
        <Typography variant="caption" color="text.secondary" component="div">
          {`Before: ${because.before}`}
        </Typography>
      )}
      {because?.after && (
        <Typography variant="caption" color="text.secondary" component="div">
          {`After: ${because.after}`}
        </Typography>
      )}
    </Box>
  );
}

function ChannelBeforeAfter({ channel }: { channel: AIProposalRenderChannel }) {
  const html = channel.format === 'html' || channel.channel === 'email';
  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="caption">
        {channel.channel} {channel.changed ? '· changed' : '· unchanged'}
      </Typography>
      {channel.subject?.before !== channel.subject?.after && (
        <Typography variant="body2">
          Subject: “{channel.subject?.before ?? ''}” → “{channel.subject?.after ?? ''}”
        </Typography>
      )}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Before
          </Typography>
          <RenderedBodyView
            body={channel.body?.before ?? channel.error?.before ?? ''}
            preview={html}
            title="Before"
            height={320}
          />
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            After
          </Typography>
          <RenderedBodyView
            body={channel.body?.after ?? channel.error?.after ?? ''}
            preview={html}
            title="After"
            height={320}
          />
        </Box>
      </Box>
    </Box>
  );
}
